"use client";
import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  User, Mail, Phone, Lock, Save, Eye, EyeOff,
  Shield, Settings as SettingsIcon, AlertCircle,
  CheckCircle, Camera, Upload, X, RotateCcw, Crop,
} from "lucide-react";
import { useSettings } from "../context/SettingsContext";
import { CDN_URL } from "../config/apiconfig";

// ─── constants ───────────────────────────────────────────────────────────────

const ADMIN_ID = localStorage.getItem("admin_id");

const STRONG_PASSWORD_RE =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

const PHONE_RE = /^[6-9]\d{9}$/;

// ─── small helpers ────────────────────────────────────────────────────────────

const isValidPhone = (phone) => PHONE_RE.test(phone.replace(/\D/g, ""));

const validatePasswordFields = ({ currentPassword, newPassword, confirmPassword }) => {
  const errs = {};
  if (!currentPassword) errs.currentPassword = "Current password is required";
  if (!newPassword) {
    errs.newPassword = "New password is required";
  } else if (!STRONG_PASSWORD_RE.test(newPassword)) {
    errs.newPassword =
      "Password must include uppercase, lowercase, number & special character";
  }
  if (newPassword !== confirmPassword) errs.confirmPassword = "Passwords do not match";
  return errs;
};

// ─── reusable sub-components ─────────────────────────────────────────────────

const Alert = ({ type, message }) => {
  if (!message) return null;
  const isError = type === "error";
  return (
    <div
      className={`mb-6 p-4 rounded-xl flex items-center gap-3 border ${
        isError
          ? "bg-red-50 border-red-200"
          : "bg-green-50 border-green-200"
      }`}
    >
      {isError ? (
        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
      ) : (
        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
      )}
      <span className={`font-medium ${isError ? "text-red-800" : "text-green-800"}`}>
        {message}
      </span>
    </div>
  );
};

const FieldError = ({ id, message }) =>
  message ? (
    <p id={id} className="mt-1 text-sm text-red-600 flex items-center gap-1">
      <AlertCircle className="w-4 h-4" />
      {message}
    </p>
  ) : null;

const PasswordInput = ({ id, label, value, onChange, show, onToggle, error, helpText }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-2">
      {label}
    </label>
    <div className="relative">
      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
      <input
        id={id}
        type={show ? "text" : "password"}
        value={value}
        onChange={onChange}
        className={`w-full pl-10 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors ${
          error ? "border-red-300" : "border-gray-300"
        }`}
        placeholder={`Enter your ${label.toLowerCase()}`}
        aria-describedby={error ? `${id}-error` : helpText ? `${id}-help` : undefined}
      />
      <button
        type="button"
        onClick={onToggle}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        aria-label={show ? "Hide password" : "Show password"}
      >
        {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
      </button>
    </div>
    {error ? (
      <FieldError id={`${id}-error`} message={error} />
    ) : helpText ? (
      <p id={`${id}-help`} className="mt-1 text-sm text-gray-500">{helpText}</p>
    ) : null}
  </div>
);

const SaveButton = ({ loading, label, loadingLabel, disabled }) => (
  <button
    type="submit"
    disabled={loading || disabled}
    className="flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-lg
               hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed
               transition-colors font-medium focus:outline-none focus:ring-2
               focus:ring-teal-500 focus:ring-offset-2"
  >
    {loading ? (
      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
    ) : (
      <Save className="w-5 h-5" />
    )}
    {loading ? loadingLabel : label}
  </button>
);

// ─── main page ────────────────────────────────────────────────────────────────

const SettingsPage = () => {
  const { getProfile, updateProfile, updatePassword, loading, getSettings, updateSettings } =
    useSettings();

  // ── state ──
  const [profileData, setProfileData] = useState({
    name: "", last_name: "", email: "", phone: "", role: "", avatar: null,
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: "", newPassword: "", confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState({
    current: false, new: false, confirm: false,
  });
  const [isDragOver, setIsDragOver] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMsg, setSuccessMsg] = useState("");
  const [sectionLoading, setSectionLoading] = useState({ profile: false, password: false });

  const fileInputRef = useRef(null);

  // ── flash success ──
  const flash = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(""), 3000);
  };

  const setError = (key, msg) => setErrors((prev) => ({ ...prev, [key]: msg }));
  const clearErrors = () => setErrors({});

  // ── fetch profile on mount ──
  useEffect(() => {
    if (!ADMIN_ID) return;
    (async () => {
      const res = await getProfile(ADMIN_ID);
      if (res?.status) {
        const d = res.data;
        setProfileData({
          name: d.first_name ?? "",
          last_name: d.last_name ?? "",
          email: d.email ?? "",
          phone: d.phone ?? "",
          role: d.role ?? "",
          avatar: d.image ? `${CDN_URL}/${d.image}` : null,
        });
      } else {
        setError("general", res?.message || "Failed to load profile");
      }
    })();
  }, []);

  // ── scroll to hash on mount ──
  useEffect(() => {
    if (!window.location.hash) return;
    const el = document.querySelector(window.location.hash);
    if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  }, []);

  // ── profile update ──
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    clearErrors();
    if (!isValidPhone(profileData.phone)) {
      setError("phone", "Please enter a valid 10-digit phone number");
      return;
    }
    setSectionLoading((p) => ({ ...p, profile: true }));
    const formData = new FormData();
    formData.append("first_name", profileData.name);
    formData.append("last_name", profileData.last_name);
    formData.append("phone", profileData.phone);
    if (profileData.avatarFile) formData.append("image", profileData.avatarFile);

    const res = await updateProfile(ADMIN_ID, formData);
    setSectionLoading((p) => ({ ...p, profile: false }));
    if (!res?.status) {
      setError("general", res?.message || "Profile update failed");
    } else {
      flash("Profile updated successfully!");
    }
  };

  // ── password change ──
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    const errs = validatePasswordFields(passwordData);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    clearErrors();
    setSectionLoading((p) => ({ ...p, password: true }));

    const res = await updatePassword(ADMIN_ID, {
      current_password: passwordData.currentPassword,
      new_password: passwordData.newPassword,
      confirm_password: passwordData.confirmPassword,
    });
    setSectionLoading((p) => ({ ...p, password: false }));

    if (!res?.status) {
      setError("password", res?.message || "Password update failed");
      return;
    }
    if (res.token) localStorage.setItem("token", res.token);
    flash(res.message || "Password updated successfully!");
    setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
    if (res.token) setTimeout(() => (window.location.href = "/login"), 2000);
  };

  // ── image helpers ──
  const applyImageFile = (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError("avatar", "File size must be less than 5MB"); return; }
    if (!file.type.startsWith("image/")) { setError("avatar", "Please select a valid image file"); return; }
    setProfileData((p) => ({ ...p, avatarFile: file, avatar: URL.createObjectURL(file) }));
  };

  const removeAvatar = () =>
    setProfileData((p) => ({ ...p, avatar: null, avatarFile: null }));

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    applyImageFile(e.dataTransfer.files[0]);
  };

  const canSubmitPassword =
    passwordData.currentPassword &&
    passwordData.newPassword &&
    passwordData.confirmPassword &&
    passwordData.newPassword === passwordData.confirmPassword;

  // ── render ──
  return (
    <main className="p-4 sm:p-6 max-w-7xl mx-auto" role="main">
      {/* Header */}
      <header className="mb-8">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center">
            <SettingsIcon className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-teal-800">Settings</h1>
            <p className="text-gray-600 mt-1">Manage your account settings and preferences</p>
          </div>
        </div>
        <Alert type="success" message={successMsg} />
        <Alert type="error" message={errors.general} />
      </header>

      <div className="space-y-8">
        {/* ── Personal Information ── */}
        <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Personal Information</h2>

          <form onSubmit={handleProfileUpdate} className="space-y-6">
            {/* Avatar upload zone */}
            <div className="flex flex-col lg:flex-row gap-6 p-6 bg-teal-50 rounded-xl border-2 border-dashed border-teal-200">
              {/* Avatar preview */}
              <div className="flex flex-col items-center lg:items-start">
                <div className="relative group">
                  <div className="w-32 h-32 rounded-2xl overflow-hidden bg-gradient-to-br from-teal-600 to-teal-800 shadow-xl border-4 border-white">
                    {profileData.avatar ? (
                      <img
                        src={profileData.avatar}
                        alt="Profile"
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-white text-4xl font-bold">
                          {profileData.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>

                  <div
                    className="absolute inset-0 bg-black/50 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Camera className="w-8 h-8 text-white" />
                  </div>

                  {profileData.avatar && (
                    <button
                      type="button"
                      onClick={removeAvatar}
                      className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg"
                      aria-label="Remove profile picture"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div className="flex gap-2 mt-4">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2 px-3 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
                  >
                    <Upload className="w-4 h-4" /> Upload
                  </button>
                  {profileData.avatar && (
                    <button
                      type="button"
                      onClick={removeAvatar}
                      className="flex items-center gap-2 px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium"
                    >
                      <X className="w-4 h-4" /> Remove
                    </button>
                  )}
                </div>
              </div>

              {/* Drag & drop zone */}
              <div className="flex-1">
                <div
                  onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                  onDragLeave={(e) => { e.preventDefault(); setIsDragOver(false); }}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-300 ${
                    isDragOver
                      ? "border-teal-500 bg-teal-50 scale-105"
                      : "border-gray-300 hover:border-teal-400"
                  }`}
                >
                  <div className="flex flex-col items-center gap-4">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-colors ${isDragOver ? "bg-teal-600" : "bg-gray-100"}`}>
                      <Upload className={`w-8 h-8 ${isDragOver ? "text-white" : "text-gray-400"}`} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {isDragOver ? "Drop your image here!" : "Update Profile Picture"}
                      </h3>
                      <p className="text-sm text-gray-600 mb-2">
                        Drag and drop an image here, or click to browse
                      </p>
                      <p className="text-xs text-gray-500">
                        Supports: JPG, PNG, GIF • Max size: 5MB • Recommended: 400×400px
                      </p>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1"><Crop className="w-3 h-3" /> Auto-crop</span>
                      <span className="flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Auto-rotate</span>
                      <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Instant preview</span>
                    </div>
                  </div>
                </div>
                <FieldError id="avatar-error" message={errors.avatar} />
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => applyImageFile(e.target.files[0])}
                className="hidden"
              />
            </div>

            {/* Form fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Email — read only */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="email"
                    value={profileData.email}
                    readOnly
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed focus:outline-none"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">Contact support to change your email</p>
              </div>

              {/* Role — read only */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                <div className="relative">
                  <Shield className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={profileData.role}
                    readOnly
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed focus:outline-none"
                  />
                </div>
              </div>

              {/* First name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    id="name"
                    type="text"
                    value={profileData.name}
                    onChange={(e) => setProfileData((p) => ({ ...p, name: e.target.value }))}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors ${errors.name ? "border-red-300" : "border-gray-300"}`}
                    placeholder="Enter your first name"
                  />
                </div>
                <FieldError id="name-error" message={errors.name} />
              </div>

              {/* Last name */}
              <div>
                <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    id="last_name"
                    type="text"
                    value={profileData.last_name}
                    onChange={(e) => setProfileData((p) => ({ ...p, last_name: e.target.value }))}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors ${errors.last_name ? "border-red-300" : "border-gray-300"}`}
                    placeholder="Enter your last name"
                  />
                </div>
                <FieldError id="last-name-error" message={errors.last_name} />
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    id="phone"
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => setProfileData((p) => ({ ...p, phone: e.target.value }))}
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-colors ${errors.phone ? "border-red-300" : "border-gray-300"}`}
                    placeholder="Enter your phone number"
                  />
                </div>
                <FieldError id="phone-error" message={errors.phone} />
              </div>
            </div>

            <div className="flex justify-end">
              <SaveButton
                loading={sectionLoading.profile}
                label="Save Changes"
                loadingLabel="Saving..."
              />
            </div>
          </form>
        </section>

        {/* ── Change Password ── */}
        <section id="password" className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Change Password</h2>

          <form onSubmit={handlePasswordChange} className="space-y-6">
            <PasswordInput
              id="currentPassword"
              label="Current Password"
              value={passwordData.currentPassword}
              onChange={(e) => setPasswordData((p) => ({ ...p, currentPassword: e.target.value }))}
              show={showPassword.current}
              onToggle={() => setShowPassword((p) => ({ ...p, current: !p.current }))}
              error={errors.currentPassword}
            />
            <PasswordInput
              id="newPassword"
              label="New Password"
              value={passwordData.newPassword}
              onChange={(e) => setPasswordData((p) => ({ ...p, newPassword: e.target.value }))}
              show={showPassword.new}
              onToggle={() => setShowPassword((p) => ({ ...p, new: !p.new }))}
              error={errors.newPassword}
              helpText="Must be 8+ chars with uppercase, lowercase, number & special character"
            />
            <PasswordInput
              id="confirmPassword"
              label="Confirm New Password"
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData((p) => ({ ...p, confirmPassword: e.target.value }))}
              show={showPassword.confirm}
              onToggle={() => setShowPassword((p) => ({ ...p, confirm: !p.confirm }))}
              error={errors.confirmPassword}
            />

            {errors.password && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {errors.password}
                </p>
              </div>
            )}

            <div className="flex justify-end">
              <SaveButton
                loading={sectionLoading.password}
                label="Update Password"
                loadingLabel="Updating..."
                disabled={!canSubmitPassword}
              />
            </div>
          </form>
        </section>
      </div>
    </main>
  );
};

export default SettingsPage;